import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router";

// Unsaved changes. Each section of the edit page saves on its own, so each reports whether it is dirty.
// While any section is dirty:
//   - a reload or a closed tab shows the browser's own "leave this page?" prompt (`beforeunload`);
//   - an in-app navigation (a link, the sidebar, `navigate(-1)`) asks first with `window.confirm`.
//
// Refine's `UnsavedChangesNotifier` does the second part with the same trick (it wraps the router's
// `navigator.push` and `navigator.go`; this router has no data-router `useBlocker`). It is not used here
// because the create page navigates on purpose right after a save, and that needs a bypass that is
// synchronous: a React state flag reaches the wrapper a few renders too late. This guard reads refs.
// The browser's Back button (a `popstate`) cannot be cancelled by either; the `beforeunload` prompt does
// not cover it either. That is a known limit of this router setup.

const LEAVE_MESSAGE = "You have unsaved changes. Leave this page and lose them?";

type UnsavedRegistry = Readonly<{
  report: (key: string, dirty: boolean) => void;
  anyDirty: boolean;
  /** Runs `navigate` without the prompt: the caller has just saved, or the user chose to discard. */
  withoutPrompt: (navigate: () => void) => void;
}>;

const UnsavedContext = createContext<UnsavedRegistry>({
  report: () => {},
  anyDirty: false,
  withoutPrompt: (navigate) => navigate(),
});

export function UnsavedChangesScope({ children }: Readonly<{ children: ReactNode }>) {
  const dirtyKeys = useRef(new Set<string>());
  const bypass = useRef(false);
  const [anyDirty, setAnyDirty] = useState(false);
  const { navigator } = useContext(NavigationContext);

  const report = useCallback((key: string, dirty: boolean) => {
    if (dirtyKeys.current.has(key) === dirty) return;
    if (dirty) dirtyKeys.current.add(key);
    else dirtyKeys.current.delete(key);
    setAnyDirty(dirtyKeys.current.size > 0);
  }, []);

  const withoutPrompt = useCallback((navigate: () => void) => {
    bypass.current = true;
    try {
      navigate();
    } finally {
      bypass.current = false;
    }
  }, []);

  useEffect(() => {
    if (!anyDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = LEAVE_MESSAGE;
      return LEAVE_MESSAGE;
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    const { push, go } = navigator;
    const confirmed = () => bypass.current || window.confirm(LEAVE_MESSAGE);
    navigator.push = (...args: Parameters<typeof push>) => {
      if (confirmed()) push(...args);
    };
    navigator.go = (...args: Parameters<typeof go>) => {
      if (confirmed()) go(...args);
    };
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      navigator.push = push;
      navigator.go = go;
    };
  }, [anyDirty, navigator]);

  const value = useMemo(
    () => ({ report, anyDirty, withoutPrompt }),
    [report, anyDirty, withoutPrompt],
  );
  return <UnsavedContext.Provider value={value}>{children}</UnsavedContext.Provider>;
}

/** A section calls this on every render with its dirty flag. The flag clears when the section unmounts. */
export function useReportDirty(key: string, dirty: boolean): void {
  const { report } = useContext(UnsavedContext);
  useEffect(() => {
    report(key, dirty);
    return () => report(key, false);
  }, [report, key, dirty]);
}

export function useAnyDirty(): boolean {
  return useContext(UnsavedContext).anyDirty;
}

export function useWithoutPrompt(): UnsavedRegistry["withoutPrompt"] {
  return useContext(UnsavedContext).withoutPrompt;
}
